import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../config/firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut
} from 'firebase/auth';
import { useAuthStore } from '../../store/authStore';
import './AuthForm.css';

const AuthForm = () => {
  const navigate = useNavigate();
  const { isAuthenticated, dbUser } = useAuthStore();

  const bgCanvasRef = useRef(null);
  const cursorGlowRef = useRef(null);

  // ---------------------------------------------------------------------------
  // UI STATES
  // ---------------------------------------------------------------------------
  const [isAuthVisible, setIsAuthVisible] = useState(false);
  const [isLogin, setIsLogin] = useState(true);

  // ---------------------------------------------------------------------------
  // FORM STATES
  // ---------------------------------------------------------------------------
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Message shown if login takes longer than expected
  const [loginMessage, setLoginMessage] = useState('');

  // Tracks whether a login operation is currently active
  const loginAttemptRef = useRef(false);

  // ---------------------------------------------------------------------------
  // AUTHENTICATION + DATABASE SYNC
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (isAuthenticated && dbUser) {
      // Firebase authentication and backend/database synchronization
      // are both complete.

      loginAttemptRef.current = false;

      setLoginMessage('');
      setIsLoading(false);

      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, dbUser, navigate]);

  // ---------------------------------------------------------------------------
  // BACKGROUND CANVAS ANIMATION
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (cursorGlowRef.current) {
        cursorGlowRef.current.style.left = `${e.clientX}px`;
        cursorGlowRef.current.style.top = `${e.clientY}px`;
      }
    };

    document.addEventListener('mousemove', handleMouseMove);

    const canvas = bgCanvasRef.current;

    if (!canvas) {
      return () => {
        document.removeEventListener(
          'mousemove',
          handleMouseMove
        );
      };
    }

    const ctx = canvas.getContext('2d');

    let w = 0;
    let h = 0;
    let particles = [];
    let animationFrameId;

    const resize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
    };

    const createParticles = () => {
      particles = [];

      const count = Math.floor((w * h) / 18000);

      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: Math.random() * 1.5 + 0.3,
          vx: (Math.random() - 0.5) * 0.15,
          vy: (Math.random() - 0.5) * 0.15,
          a: Math.random() * 0.5 + 0.1,
          color:
            Math.random() > 0.5
              ? '110,231,247'
              : '167,139,250'
        });
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);

      const blobs = [
        {
          x: w * 0.15,
          y: h * 0.2,
          r: 280,
          c: '110,231,247',
          a: 0.04
        },
        {
          x: w * 0.85,
          y: h * 0.7,
          r: 320,
          c: '167,139,250',
          a: 0.04
        },
        {
          x: w * 0.5,
          y: h * 0.5,
          r: 200,
          c: '52,211,153',
          a: 0.02
        }
      ];

      blobs.forEach((b) => {
        const g = ctx.createRadialGradient(
          b.x,
          b.y,
          0,
          b.x,
          b.y,
          b.r
        );

        g.addColorStop(0, `rgba(${b.c},${b.a})`);
        g.addColorStop(1, `rgba(${b.c},0)`);

        ctx.beginPath();
        ctx.arc(
          b.x,
          b.y,
          b.r,
          0,
          Math.PI * 2
        );
        ctx.fillStyle = g;
        ctx.fill();
      });

      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;

        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;

        ctx.beginPath();
        ctx.arc(
          p.x,
          p.y,
          p.r,
          0,
          Math.PI * 2
        );
        ctx.fillStyle = `rgba(${p.color},${p.a})`;
        ctx.fill();
      });

      animationFrameId =
        requestAnimationFrame(draw);
    };

    const handleResize = () => {
      resize();
      createParticles();
    };

    resize();
    createParticles();
    draw();

    window.addEventListener(
      'resize',
      handleResize
    );

    return () => {
      document.removeEventListener(
        'mousemove',
        handleMouseMove
      );

      window.removeEventListener(
        'resize',
        handleResize
      );

      cancelAnimationFrame(
        animationFrameId
      );
    };
  }, []);

  // ---------------------------------------------------------------------------
  // LOGIN / CREATE ACCOUNT
  // ---------------------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    setError('');
    setSuccessMsg('');
    setLoginMessage('');
    setIsLoading(true);

    loginAttemptRef.current = isLogin;

    let messageTimer1 = null;
    let messageTimer2 = null;

    try {
      // Give React a moment to render the loading state,
      // especially when Firebase responds very quickly locally.
      await new Promise((resolve) =>
        setTimeout(resolve, 50)
      );

      if (isLogin) {
        // -------------------------------------------------------------
        // LOGIN
        // -------------------------------------------------------------

        messageTimer1 = setTimeout(() => {
          if (loginAttemptRef.current) {
            setLoginMessage(
              'Your login is taking a little longer than usual. Please stay on this page while we reconnect to the server.'
            );
          }
        }, 5000);

        messageTimer2 = setTimeout(() => {
          if (loginAttemptRef.current) {
            setLoginMessage(
              'Thanks for your patience. The server is taking a little longer to respond. Your login is still in progress.'
            );
          }
        }, 20000);

        await signInWithEmailAndPassword(
          auth,
          email,
          password
        );

        // IMPORTANT:
        // Do not set isLoading(false) here.
        //
        // Firebase authentication may finish before
        // your backend/dbUser synchronization finishes.
        //
        // The useEffect above will finish the loading state
        // once both authentication and dbUser are ready.

      } else {
        // -------------------------------------------------------------
        // CREATE ACCOUNT
        // -------------------------------------------------------------

        await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );

        // Firebase automatically signs the new user in.
        // Sign them out so they can manually log in.
        await signOut(auth);

        setSuccessMsg(
          'Account created successfully! Please log in.'
        );

        setIsLogin(true);
        setPassword('');
        setIsLoading(false);
        loginAttemptRef.current = false;
      }

    } catch (err) {
      console.error('Authentication error:', err);

      loginAttemptRef.current = false;

      // -----------------------------------------------------------------------
      // PROFESSIONAL USER-FACING ERROR MESSAGES
      // Never show Firebase error codes directly to users.
      // -----------------------------------------------------------------------
      let friendlyMessage =
        'Unable to complete your request. Please try again.';

      switch (err?.code) {
        case 'auth/invalid-credential':
        case 'auth/wrong-password':
        case 'auth/user-not-found':
          friendlyMessage =
            'Unable to sign in. Please check your email and password and try again.';
          break;

        case 'auth/invalid-email':
          friendlyMessage =
            'Please enter a valid email address.';
          break;

        case 'auth/user-disabled':
          friendlyMessage =
            'This account has been disabled. Please contact support.';
          break;

        case 'auth/too-many-requests':
          friendlyMessage =
            'Too many sign-in attempts. Please wait a moment and try again.';
          break;

        case 'auth/network-request-failed':
          friendlyMessage =
            'We were unable to connect. Please check your internet connection and try again.';
          break;

        case 'auth/email-already-in-use':
          friendlyMessage =
            'An account with this email already exists. Please sign in instead.';
          break;

        case 'auth/weak-password':
          friendlyMessage =
            'Your password is not strong enough. Please choose a stronger password.';
          break;

        case 'auth/requires-recent-login':
          friendlyMessage =
            'For security reasons, please sign in again and try once more.';
          break;

        default:
          friendlyMessage =
            isLogin
              ? 'Unable to sign in right now. Please try again.'
              : 'Unable to create your account right now. Please try again.';
          break;
      }

      setError(friendlyMessage);
      setIsLoading(false);
      setLoginMessage('');
    } finally {
      if (messageTimer1) {
        clearTimeout(messageTimer1);
      }

      if (messageTimer2) {
        clearTimeout(messageTimer2);
      }

      // Do not clear loginMessage here after a successful login.
      // We still need to wait for dbUser.
      if (!isLogin) {
        setLoginMessage('');
      }
    }
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <div id="landing-app">

      <canvas
        id="bg-canvas"
        ref={bgCanvasRef}
      ></canvas>

      <div
        id="cursor-glow"
        ref={cursorGlowRef}
      ></div>

      <nav className="landing-nav">

        <div className="nav-brand">
          CONSCIA
        </div>

        {!isAuthVisible && (
          <button
            className="nav-login-btn"
            onClick={() =>
              setIsAuthVisible(true)
            }
          >
            Login
          </button>
        )}

      </nav>

      <main className="landing-main">

        {!isAuthVisible ? (

          <div className="hero-section">

            <div className="hero-badge">
              AI Powered Ethical Shopping Companion
            </div>

            <h1 className="hero-title">
              Ethical transparency <br />

              <span className="hero-accent">
                before you buy.
              </span>
            </h1>

            <p className="hero-description">
              Bridge the intention-action gap in
              e-commerce. Conscia utilizes a
              Teacher-Student neural network and
              Explainable AI (LIME) to analyze
              real-time consumer reviews,
              exposing greenwashing and
              delivering transparent scores
              across Environmental Impact,
              Labor Rights, Animal Welfare,
              and Corporate Governance.
            </p>

            <div className="hero-actions">

              <button
                className="btn-primary-large"
                onClick={() =>
                  setIsAuthVisible(true)
                }
              >
                Access Dashboard
                <span className="arrow">
                  →
                </span>
              </button>

            </div>

          </div>

        ) : (

          <div className="auth-section">

            <div className="auth-card">

              <button
                className="back-btn"
                disabled={isLoading}
                onClick={() => {
                  setIsAuthVisible(false);
                  setError('');
                  setSuccessMsg('');
                  setLoginMessage('');
                }}
              >
                ← Back
              </button>

              <div className="auth-header">

                <h2>
                  {isLogin
                    ? 'Login'
                    : 'Create an account'}
                </h2>

                <p>
                  {isLogin
                    ? 'Welcome back to Conscia'
                    : 'Join us and start tracking'}
                </p>

              </div>

              {/* -------------------------------------------------------------
                  SUCCESS MESSAGE
              ------------------------------------------------------------- */}
              {successMsg && (
                <div
                  style={{
                    background:
                      'rgba(52,211,153,0.1)',
                    color: '#34d399',
                    border:
                      '1px solid rgba(52,211,153,0.3)',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    marginBottom: '1.5rem',
                    textAlign: 'center',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                >
                  {successMsg}
                </div>
              )}

              {/* -------------------------------------------------------------
                  ERROR MESSAGE
              ------------------------------------------------------------- */}
              {error && (
                <div className="auth-error">
                  {error}
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                className="auth-form"
              >

                {/* EMAIL */}
                <div className="form-group">

                  <label>
                    Email address
                  </label>

                  <input
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) =>
                      setEmail(e.target.value)
                    }
                    disabled={isLoading}
                    required
                  />

                </div>

                {/* PASSWORD */}
                <div className="form-group">

                  <label>
                    Password
                  </label>

                  <input
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) =>
                      setPassword(e.target.value)
                    }
                    disabled={isLoading}
                    required
                  />

                </div>

                {/* -----------------------------------------------------------
                    SUBMIT BUTTON
                ----------------------------------------------------------- */}
                <button
                  type="submit"
                  className="auth-submit-btn"
                  style={{
                    minHeight: '52px'
                  }}
                  disabled={isLoading}
                >

                  {isLoading ? (

                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px'
                      }}
                    >

                      <div className="btn-spinner"></div>

                      <span>
                        {isLogin
                          ? 'Signing in...'
                          : 'Creating...'}
                      </span>

                    </div>

                  ) : (

                    isLogin
                      ? 'Login'
                      : 'Create'

                  )}

                </button>

                {/* -----------------------------------------------------------
                    SLOW LOGIN / SERVER WAKE-UP MESSAGE
                ----------------------------------------------------------- */}
                {isLoading &&
                  isLogin &&
                  loginMessage && (

                    <div
                      style={{
                        marginTop: '14px',
                        padding: '12px 14px',
                        borderRadius: '10px',
                        background:
                          'rgba(110,231,247,0.08)',
                        border:
                          '1px solid rgba(110,231,247,0.20)',
                        color:
                          'rgba(255,255,255,0.78)',
                        fontSize: '0.85rem',
                        lineHeight: '1.5',
                        textAlign: 'center'
                      }}
                    >
                      {loginMessage}
                    </div>

                  )}

              </form>

              {/* -------------------------------------------------------------
                  LOGIN / CREATE TOGGLE
              ------------------------------------------------------------- */}
              <div className="auth-toggle">

                {isLogin
                  ? 'Need to create an account? '
                  : 'Already have an account? '}

                <button
                  className="toggle-btn"
                  disabled={isLoading}
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError('');
                    setSuccessMsg('');
                    setLoginMessage('');
                  }}
                >
                  {isLogin
                    ? 'Create an account'
                    : 'Login'}
                </button>

              </div>

            </div>

          </div>

        )}

      </main>

    </div>
  );
};

export default AuthForm;